/**
 * Pull-to-Refresh Hook
 *
 * Provides pull-to-refresh functionality similar to mobile social media apps
 * Works with both touch and mouse events
 */

import { useEffect, useRef, useState, useCallback } from 'react';

interface UsePullToRefreshOptions {
  onRefresh: () => Promise<void>;
  pullThreshold?: number;
  maxPullDistance?: number;
  refreshing?: boolean;
  disabled?: boolean;
}

export function usePullToRefresh({
  onRefresh,
  pullThreshold = 80,
  maxPullDistance = 150,
  refreshing: externalRefreshing = false,
  disabled = false,
}: UsePullToRefreshOptions) {
  const [isPulling, setIsPulling] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const startY = useRef(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleTouchStart = useCallback((e: TouchEvent) => {
    // Don't start if disabled or if a modal is open
    if (disabled) return;

    // Check if any modal/dialog is currently open (common modal patterns)
    const hasOpenModal = document.querySelector('[role="dialog"]') !== null ||
                         document.querySelector('.modal') !== null ||
                         document.querySelector('[data-state="open"]') !== null;

    if (hasOpenModal) return;

    // Walk up from the touch target to find the nearest scrollable ancestor
    // (e.g. the message list inside the conversation thread). If one exists and
    // it can scroll up, defer to its native scrolling instead of engaging
    // pull-to-refresh. Pull-to-refresh only fires when the touched scroll
    // container is already at scrollTop === 0.
    const main = containerRef.current?.closest('main') ?? null;
    let scrollTop: number;
    let node: Element | null = e.target as Element | null;
    let innerScrollable: Element | null = null;

    while (node && node !== main) {
      if (node.scrollHeight > node.clientHeight) {
        const overflowY = window.getComputedStyle(node).overflowY;
        if (overflowY === 'auto' || overflowY === 'scroll') {
          innerScrollable = node;
          break;
        }
      }
      node = node.parentElement;
    }

    if (innerScrollable) {
      scrollTop = innerScrollable.scrollTop;
    } else if (main) {
      scrollTop = main.scrollTop;
    } else {
      scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    }

    if (scrollTop === 0) {
      startY.current = e.touches[0].clientY;
      setIsPulling(true);
    }
  }, [disabled]);

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!isPulling) return;

    const currentY = e.touches[0].clientY;
    const distance = currentY - startY.current;

    if (distance > 0) {
      // Prevent default scroll while pulling
      e.preventDefault();

      // Apply diminishing returns for pull distance
      const actualDistance = Math.min(
        distance * 0.5, // Damping effect
        maxPullDistance
      );

      setPullDistance(actualDistance);
    }
  }, [isPulling, maxPullDistance]);

  const handleTouchEnd = useCallback(async () => {
    if (!isPulling) return;

    setIsPulling(false);

    if (pullDistance >= pullThreshold && !isRefreshing) {
      setIsRefreshing(true);

      try {
        await onRefresh();
      } catch (error) {
        console.error('Refresh failed:', error);
      } finally {
        setIsRefreshing(false);
        setPullDistance(0);
      }
    } else {
      // Animate back to 0
      setPullDistance(0);
    }
  }, [isPulling, pullDistance, pullThreshold, isRefreshing, onRefresh]);

  // Mouse events for desktop testing
  const handleMouseDown = useCallback((e: MouseEvent) => {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    if (scrollTop === 0) {
      startY.current = e.clientY;
      setIsPulling(true);
    }
  }, []);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isPulling) return;

    const currentY = e.clientY;
    const distance = currentY - startY.current;

    if (distance > 0) {
      e.preventDefault();
      const actualDistance = Math.min(distance * 0.5, maxPullDistance);
      setPullDistance(actualDistance);
    }
  }, [isPulling, maxPullDistance]);

  const handleMouseUp = useCallback(async () => {
    if (!isPulling) return;

    setIsPulling(false);

    if (pullDistance >= pullThreshold && !isRefreshing) {
      setIsRefreshing(true);

      try {
        await onRefresh();
      } catch (error) {
        console.error('Refresh failed:', error);
      } finally {
        setIsRefreshing(false);
        setPullDistance(0);
      }
    } else {
      setPullDistance(0);
    }
  }, [isPulling, pullDistance, pullThreshold, isRefreshing, onRefresh]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Don't set up if disabled
    if (disabled) return;

    // Only enable pull-to-refresh on touch devices (mobile/tablet)
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

    if (!isTouchDevice) {
      // Not a touch device, skip pull-to-refresh setup
      return;
    }

    // Touch events only (mobile/tablet)
    container.addEventListener('touchstart', handleTouchStart, { passive: false });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd);

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd, disabled]);

  return {
    containerRef,
    isPulling,
    pullDistance,
    isRefreshing: isRefreshing || externalRefreshing,
    shouldTriggerRefresh: pullDistance >= pullThreshold,
  };
}
